import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import type { Tenant, TenantUser } from '@/types/saas';

export const useTenant = () => {
  const { user } = useAuth();
  const { user: supabaseUser } = useSupabaseAuth();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [tenantUser, setTenantUser] = useState<TenantUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    if (supabaseUser) {
      loadTenantData();
      checkSuperAdmin();
    } else {
      setLoading(false);
    }
  }, [supabaseUser]);

  const loadTenantData = async () => {
    try {
      // Get tenant user relationship
      const { data: tenantUserData, error: tenantUserError } = await supabase
        .from('tenant_users')
        .select('*')
        .eq('user_id', supabaseUser?.id)
        .single();

      if (tenantUserData) {
        setTenantUser(tenantUserData as TenantUser);

        // Get tenant details
        const { data: tenantData, error: tenantError } = await supabase
          .from('tenants')
          .select('*')
          .eq('id', tenantUserData.tenant_id)
          .single();

        if (tenantData) {
          setCurrentTenant(tenantData as Tenant);
        }
      }
    } catch (error) {
      console.error('Error loading tenant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSuperAdmin = async () => {
    try {
      const { data, error } = await supabase
        .rpc('is_super_admin');
      
      if (data) {
        setIsSuperAdmin(true);
      }
    } catch (error) {
      console.error('Error checking super admin status:', error);
    }
  };

  const createTenant = async (companyName: string, planId: string) => {
    try {
      // Create tenant
      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .insert({
          company_name: companyName,
          subscription_plan_id: planId,
          subdomain: companyName.toLowerCase().replace(/\s+/g, '-')
        })
        .select()
        .single();

      if (tenantError) throw tenantError;

      // Add current user as owner
      const { error: userError } = await supabase
        .from('tenant_users')
        .insert({
          tenant_id: tenant.id,
          user_id: supabaseUser?.id,
          role: 'owner',
          is_owner: true
        });

      if (userError) throw userError;

      await loadTenantData();
      return { success: true, tenant };
    } catch (error) {
      console.error('Error creating tenant:', error);
      return { success: false, error };
    }
  };

  const switchTenant = async (tenantId: string) => {
    try {
      const { data: tenantData, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', tenantId)
        .single();

      if (error) throw error;

      setCurrentTenant(tenantData as Tenant);
      
      // Reload the page to apply new tenant context
      window.location.reload();
    } catch (error) {
      console.error('Error switching tenant:', error);
    }
  };

  return {
    currentTenant,
    tenantUser,
    loading,
    isSuperAdmin,
    isOwner: tenantUser?.is_owner || false,
    isAdmin: tenantUser?.role === 'admin' || tenantUser?.is_owner || false,
    createTenant,
    switchTenant,
    reload: loadTenantData
  };
};